<template>
    <!-- An empty selection means every account: the API resolves them all when
         sims_id is left out. The "All" row makes that visible in the open panel. -->
    <MultiSelect :inputId="inputId" :modelValue="modelValue" :options="options"
        optionLabel="sims_account" optionValue="sims_account"
        :placeholder="$t('taskDetail.allOption')" :maxSelectedLabels="2"
        :selectedItemsLabel="$t('modal.selectedCount')" filter :showToggleAll="false"
        scrollHeight="min(400px, 40vh)" panelClass="sims-account-panel"
        class="w-100 bg-body-tertiary"
        @update:modelValue="$emit('update:modelValue', $event)"
        @change="$emit('change')">
        <template #header>
            <div class="sims-all-option" role="option" :aria-selected="!modelValue.length" @click="selectAll">
                <Checkbox :modelValue="!modelValue.length" :binary="true" :tabindex="-1" />
                <span>{{ $t('taskDetail.allOption') }}</span>
            </div>
        </template>
        <template #option="slotProps">
            <span>{{ slotProps.option.sims_account }}<span v-if="slotProps.option.comment"
                class="text-body-secondary"> — {{ slotProps.option.comment }}</span></span>
        </template>
    </MultiSelect>
</template>

<script>
import MultiSelect from 'primevue/multiselect';
import Checkbox from 'primevue/checkbox';

export default {
    name: 'SimsAccountSelect',
    components: {
        MultiSelect,
        Checkbox
    },
    props: {
        modelValue: {
            type: Array,
            required: true
        },
        options: {
            type: Array,
            required: true
        },
        inputId: {
            type: String,
            default: null
        }
    },
    emits: ['update:modelValue', 'change'],
    methods: {
        selectAll() {
            // Already "All" -- nothing to clear, so no refetch either.
            if (!this.modelValue.length) return;
            this.$emit('update:modelValue', []);
            this.$emit('change');
        }
    }
};
</script>

<style scoped>
.sims-all-option {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem 0.75rem;
    cursor: pointer;
    border-bottom: 1px solid var(--bs-border-color);
}

.sims-all-option:hover {
    background: var(--bs-tertiary-bg);
}
</style>

<!-- Not scoped: the panel is portalled to <body>, outside this component. -->
<style>
/* Match the small text of the signals table. The theme's .p-component and
   .p-inputtext pin 1rem on the panel, the list (<ul>) and the search box alike,
   so each is set rather than inherited. */
.sims-account-panel.p-multiselect-panel,
.sims-account-panel .p-multiselect-items,
.sims-account-panel .p-multiselect-filter {
    font-size: 0.875rem;
}

/* On phones the panel covers the screen like a native picker instead of
   opening beside the field, where it could run off the edge. */
@media (max-width: 575.98px) {
    .sims-account-panel {
        position: fixed !important;
        /* Beats the inline top/left/min-width PrimeVue sets when aligning. */
        inset: 0 !important;
        min-width: 0 !important;
        border-radius: 0;
        /* A fixed element has no offsetParent, which PrimeVue reads as hidden:
           it measures it via getHiddenElementDimensions() and leaves an inline
           display:none behind. */
        display: flex !important;
        flex-direction: column;
    }

    .sims-account-panel .p-multiselect-items-wrapper {
        /* Beats the inline scrollHeight; the list takes the rest of the screen. */
        max-height: none !important;
        flex: 1 1 auto;
        /* PrimeVue closes the panel on page scroll, so the list's must not chain. */
        overscroll-behavior: contain;
    }
}
</style>
